import { useState } from 'react';
import usePageTitle from '../hooks/usePageTitle';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2, Plus, X } from 'lucide-react';
import {
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory,
} from '../api/categories';
import Modal from '../components/Modal';
import Pagination from '../components/Pagination';
import FilterBar from '../components/FilterBar';
import { useAuth } from '../context/AuthContext';
import { canWrite } from '../utils/permissions';
import { useTranslation } from 'react-i18next';

const EMPTY_FORM = { name: '', description: '', sortOrder: 0, imageUrl: '', subcategories: [] };
const EMPTY_SUB = { name: '', sortOrder: 0, imageUrl: '' };

// Thumbnail for a public image URL; hides itself if the URL doesn't load.
function Thumb({ url, size = 'w-8 h-8' }) {
  if (!url) return null;
  return <img src={url} alt="" className={`${size} rounded-md object-cover border border-gray-200 shrink-0`} onError={(e) => { e.currentTarget.style.display = 'none'; }} />;
}

export default function Categories() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [modal, setModal] = useState(null); // null | { mode: 'create' | 'edit', data? }
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null); // category to confirm delete

  usePageTitle('Categories');
  const { t } = useTranslation();
  const { user } = useAuth();
  const write = canWrite(user?.role);
  const [filters, setFilters] = useState({});
  const handleFilter = (key, val) => { setFilters((f) => ({ ...f, [key]: val || undefined })); setPage(0); };
  const clearFilters = () => { setFilters({}); setPage(0); };
  const { data, isLoading } = useQuery({
    queryKey: ['categories', page, filters],
    queryFn: () => getCategories(page, 10, filters).then((r) => r.data.data),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['categories'] });

  const saveMutation = useMutation({
    mutationFn: (payload) =>
      modal?.mode === 'edit'
        ? updateCategory(modal.data.id, payload)
        : createCategory(payload),
    onSuccess: () => { invalidate(); closeModal(); },
    onError: (err) => setFormError(err.response?.data?.message || 'Something went wrong.'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteCategory(id),
    onSuccess: () => { invalidate(); setDeleteTarget(null); },
  });

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setFormError('');
    setModal({ mode: 'create' });
  };

  const openEdit = (cat) => {
    setForm({
      name: cat.name,
      description: cat.description || '',
      sortOrder: cat.sortOrder ?? 0,
      imageUrl: cat.imageUrl || '',
      subcategories: (cat.subcategories || []).map((s) => ({ id: s.id, name: s.name, sortOrder: s.sortOrder ?? 0, imageUrl: s.imageUrl || '' })),
    });
    setFormError('');
    setModal({ mode: 'edit', data: cat });
  };

  const closeModal = () => setModal(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError('');
    saveMutation.mutate({
      name: form.name,
      description: form.description || null,
      sortOrder: parseInt(form.sortOrder) || 0,
      imageUrl: form.imageUrl.trim() || null,
      // Rows left without a name are ignored; the list replaces the category's subcategories.
      subcategories: form.subcategories
        .filter((s) => s.name.trim())
        .map((s) => ({ id: s.id ?? null, name: s.name.trim(), sortOrder: parseInt(s.sortOrder) || 0, imageUrl: s.imageUrl.trim() || null })),
    });
  };

  const setSub = (idx, key, value) =>
    setForm((f) => ({ ...f, subcategories: f.subcategories.map((s, i) => (i === idx ? { ...s, [key]: value } : s)) }));
  const addSub = () => setForm((f) => ({ ...f, subcategories: [...f.subcategories, { ...EMPTY_SUB }] }));
  const removeSub = (idx) => setForm((f) => ({ ...f, subcategories: f.subcategories.filter((_, i) => i !== idx) }));

  const categories = data?.content ?? [];
  const totalPages = data?.totalPages ?? 0;

  return (
    <div className="p-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">{t('categories.title')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{data?.totalElements ?? 0} {t('common.total')}</p>
        </div>
        <div className="flex items-center gap-2">
          {write && (
            <button onClick={openCreate} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
              <Plus size={15} /> {t('categories.new')}
            </button>
          )}
        </div>
      </div>

      <FilterBar
        fields={[{ key: 'name', type: 'text', label: 'Search name…' }]}
        values={filters} onChange={handleFilter} onClear={clearFilters}
      />
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="py-16 text-center text-sm text-gray-400">{t('common.loading')}</div>
        ) : categories.length === 0 ? (
          <div className="py-16 text-center text-sm text-gray-400">{t('categories.noCategories')}</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50 text-left">
                <th className="px-4 py-3 font-medium text-gray-500">{t('common.name')}</th>
                <th className="px-4 py-3 font-medium text-gray-500">{t('categories.subcategories')}</th>
                <th className="px-4 py-3 font-medium text-gray-500">{t('common.description')}</th>
                <th className="px-4 py-3 font-medium text-gray-500 text-right">{t('categories.sortOrder')}</th>
                <th className="px-4 py-3 w-28" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {categories.map((cat) => (
                <tr key={cat.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-gray-800">
                    <div className="flex items-center gap-2"><Thumb url={cat.imageUrl} />{cat.name}</div>
                  </td>
                  <td className="px-4 py-3">
                    {cat.subcategories?.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {cat.subcategories.map((s) => (
                          <span key={s.id} className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{s.name}</span>
                        ))}
                      </div>
                    ) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-500">
                    {cat.description || <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-right">{cat.sortOrder ?? 0}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      {write && <>
                        <button onClick={() => openEdit(cat)} className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"><Pencil size={14} /></button>
                        <button onClick={() => setDeleteTarget(cat)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"><Trash2 size={14} /></button>
                      </>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />

      {/* Create / Edit Modal */}
      {modal && (
        <Modal
          title={modal.mode === 'create' ? t('categories.new') : t('common.edit') + ' ' + t('categories.title')}
          onClose={closeModal}
          wide
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.name')} <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                autoFocus
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('categories.namePlaceholder')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.description')}</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder={t('categories.descriptionPlaceholder')}
              />
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('categories.sortOrder')}</label>
                <input type="number" step="1" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
              <div className="col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('categories.imageUrl')}</label>
                <div className="flex items-center gap-2">
                  <input type="url" value={form.imageUrl} onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
                    placeholder="https://…" className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  <Thumb url={form.imageUrl} size="w-9 h-9" />
                </div>
              </div>
            </div>

            {/* Subcategories */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('categories.subcategories')}</label>
              {form.subcategories.length > 0 && (
                <div className="space-y-2">
                  <div className="flex gap-2 text-xs text-gray-400 px-1">
                    <span className="flex-1">{t('common.name')}</span>
                    <span className="w-20">{t('categories.sortOrder')}</span>
                    <span className="flex-1">{t('categories.imageUrl')}</span>
                    <span className="w-8" />
                  </div>
                  {form.subcategories.map((s, idx) => (
                    <div key={s.id ?? `new-${idx}`} className="flex items-center gap-2">
                      <input type="text" value={s.name} onChange={(e) => setSub(idx, 'name', e.target.value)}
                        maxLength={100} placeholder={t('categories.subcategoryPlaceholder')} className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                      <input type="number" step="1" value={s.sortOrder} onChange={(e) => setSub(idx, 'sortOrder', e.target.value)}
                        className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                      <input type="url" value={s.imageUrl} onChange={(e) => setSub(idx, 'imageUrl', e.target.value)}
                        placeholder="https://…" className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                      <button type="button" onClick={() => removeSub(idx)} className="w-8 p-2 text-gray-400 hover:text-red-500 transition-colors">
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <button type="button" onClick={addSub} className="mt-2 flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium">
                <Plus size={14} /> {t('categories.addSubcategory')}
              </button>
            </div>

            {formError && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {formError}
              </p>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={closeModal} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors">{t('common.cancel')}</button>
              <button type="submit" disabled={saveMutation.isPending} className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors">
                {saveMutation.isPending ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Delete Confirmation */}
      {deleteTarget && (
        <Modal title={t('categories.deleteCategory')} onClose={() => setDeleteTarget(null)}>
          <p className="text-sm text-gray-600">
            <span className="font-medium text-gray-900">{deleteTarget.name}</span>? {t('common.cannotBeUndone')}
          </p>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors">{t('common.cancel')}</button>
            <button onClick={() => deleteMutation.mutate(deleteTarget.id)} disabled={deleteMutation.isPending}
              className="px-4 py-2 text-sm font-medium bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg transition-colors">
              {deleteMutation.isPending ? t('common.deleting') : t('common.delete')}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
